import React from "react";
import * as XLSX from "xlsx";
import { Result } from "antd";
import { ButtonItem } from "../../../../Components/Items";
import ImportadorExcelGenerico from "../../../../Components/ImportadorExcelGenerico";

const columnasPreview = [
  { title: "Clave", dataIndex: "clave", key: "clave", width: 100 },
  { title: "Nombre", dataIndex: "nombre", key: "nombre", width: 160 },
  { title: "Descripción", dataIndex: "descripcion", key: "descripcion", width: 200 },
  { title: "Secuencia", dataIndex: "secuencia", key: "secuencia", width: 100 },
  { title: "Secuencia Recorrido", dataIndex: "secuenciaRecorrido", key: "secuenciaRecorrido", width: 150 },
  { title: "Cant. Máxima", dataIndex: "cantidadMaxima", key: "cantidadMaxima", width: 110 },
  { title: "Ancho", dataIndex: "ancho", key: "ancho", width: 80 },
  { title: "Alto", dataIndex: "alto", key: "alto", width: 80 },
  { title: "Largo", dataIndex: "largo", key: "largo", width: 80 },
  { title: "Número Rack", dataIndex: "numeroRack", key: "numeroRack", width: 100 },
  { title: "Nivel Rack", dataIndex: "nivelRack", key: "nivelRack", width: 100 },
  { title: "Observaciones", dataIndex: "observaciones", key: "observaciones", width: 200 },
];

const mismoProyecto = (proyectoIdUbicacion, proyectoId) =>
  proyectoIdUbicacion === proyectoId ||
  proyectoIdUbicacion?.toString?.() === proyectoId;

/**
 * Genera la plantilla `plantillaUbicaciones.xlsx` para un almacén (proyecto) en específico:
 *   - Hoja 1 "Ubicaciones" con las columnas esperadas y una fila de ejemplo.
 *   - Hoja 2 "Existentes" con las claves/nombres ya registrados en ESE almacén.
 */
const construirPlantilla = (proyectoId) => (catalogos) => {
  const wb = XLSX.utils.book_new();

  const ejemplo = [{
    Clave: "A1",
    Nombre: "Rack 1 Nivel 1",
    Descripcion: "Primer nivel del rack 1",
    Secuencia: 1,
    "Secuencia Recorrido": 1,
    "Cantidad Maxima": 100,
    Ancho: 1.2,
    Alto: 2,
    Largo: 1,
    "Numero Rack": 1,
    "Nivel Rack": 1,
    Observaciones: "",
  }];
  const hojaPrincipal = XLSX.utils.json_to_sheet(ejemplo);
  XLSX.utils.book_append_sheet(wb, hojaPrincipal, "Ubicaciones");

  const existentes = (catalogos.ubicacionesExistentes || [])
    .filter((u) => mismoProyecto(u.proyectoId, proyectoId))
    .map((u) => ({ Clave: u.clave, Nombre: u.nombre }));
  const hojaExistentes =
    existentes.length > 0
      ? XLSX.utils.json_to_sheet(existentes)
      : XLSX.utils.json_to_sheet([{ Clave: "(sin ubicaciones registradas en este almacén)" }]);
  XLSX.utils.book_append_sheet(wb, hojaExistentes, "Existentes");

  return wb;
};

const camposNumericos = [
  ["Secuencia", "secuencia"],
  ["Secuencia Recorrido", "secuenciaRecorrido"],
  ["Cantidad Maxima", "cantidadMaxima"],
  ["Ancho", "ancho"],
  ["Alto", "alto"],
  ["Largo", "largo"],
];

/**
 * Valida cada fila del Excel de Ubicaciones para el almacén (proyectoId) seleccionado.
 * Solo Clave y Nombre son obligatorios (igual que el modal manual); el resto es opcional.
 */
const validarFila = (proyectoId) => (fila, catalogos, _indice, contexto) => {
  const errores = [];
  const clave = fila["Clave"] ? String(fila["Clave"]).trim() : "";
  const nombre = fila["Nombre"] ? String(fila["Nombre"]).trim() : "";
  const descripcion = fila["Descripcion"] ?? fila["Descripción"];
  const descripcionLimpia = descripcion ? String(descripcion).trim() : "";
  const numeroRack = fila["Numero Rack"] ?? fila["Número Rack"];
  const nivelRack = fila["Nivel Rack"];
  const observaciones = fila["Observaciones"] ? String(fila["Observaciones"]).trim() : "";

  if (!clave) errores.push({ campo: "Clave", motivo: "está vacía" });
  if (!nombre) errores.push({ campo: "Nombre", motivo: "está vacío" });

  const valoresNumericos = {};
  camposNumericos.forEach(([columna, campo]) => {
    const valor = fila[columna];
    if (valor === undefined || valor === null || valor === "") {
      return; // campo opcional, se deja sin definir
    }
    if (isNaN(Number(valor))) {
      errores.push({ campo: columna, motivo: "debe ser un número" });
    } else {
      valoresNumericos[campo] = Number(valor);
    }
  });

  if (clave) {
    const llave = clave.toLowerCase();
    const existeYa = (catalogos.ubicacionesExistentes || []).some(
      (u) => mismoProyecto(u.proyectoId, proyectoId) && (u.clave || "").trim().toLowerCase() === llave,
    );
    if (existeYa) {
      errores.push({ campo: "Clave", motivo: "ya existe una ubicación con esa clave en este almacén" });
    }

    if (contexto.clavesYaUsadas.has(llave)) {
      errores.push({
        campo: "Clave",
        motivo: `clave repetida en el archivo (también está en la fila ${contexto.clavesYaUsadas.get(llave)})`,
      });
    } else {
      contexto.clavesYaUsadas.set(llave, (fila["__rowNum__"] ?? 0) + 1);
    }
  }

  if (errores.length > 0) {
    return { ok: false, errores };
  }

  return {
    ok: true,
    datos: {
      clave,
      nombre,
      descripcion: descripcionLimpia || undefined,
      numeroRack: numeroRack !== undefined && numeroRack !== "" ? Number(numeroRack) : undefined,
      nivelRack: nivelRack !== undefined && nivelRack !== "" ? Number(nivelRack) : undefined,
      observaciones,
      ...valoresNumericos,
    },
  };
};

const ImportarUbicaciones = (props) => {
  const proyectoId = props.location?.state?.proyectoId;
  const proyecto = props.location?.state?.proyecto;

  if (!proyectoId) {
    return (
      <Result
        status="warning"
        title="Selecciona primero un almacén"
        subTitle="Entra a Ubicaciones, elige un almacén en el selector y usa el botón Importar Ubicaciones desde ahí."
        extra={
          <ButtonItem type="primary" onClick={() => props.history.push("/panel/ubicaciones")}>
            Ir a Ubicaciones
          </ButtonItem>
        }
      />
    );
  }

  return (
    <ImportadorExcelGenerico
      {...props}
      titulo={`Importar Ubicaciones — Almacén: ${proyecto || ""}`}
      nombreEntidadPlural="ubicaciones"
      endpointDatosImportar="ubicacionProyecto/datosParaImportar"
      endpointImportar="ubicacionProyecto/importarUbicaciones"
      nombrePlantilla="plantillaUbicaciones.xlsx"
      construirPlantilla={construirPlantilla(proyectoId)}
      validarFila={validarFila(proyectoId)}
      columnasPreview={columnasPreview}
      prepararPayloadExtra={() => ({ proyectoId })}
    />
  );
};

export default ImportarUbicaciones;
